import type { QuestionDto } from "@eb/shared";
import { Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import { optionLetter } from "../lib/format";
import { Badge, cx } from "./ui";

export interface QuestionViewProps {
    question: QuestionDto;
    /** The option the learner picked, if any. */
    selectedOptionId?: number | null;
    /** Reveal which option is correct. Requires the question to carry `isCorrect`. */
    revealed?: boolean;
    /** Omit to render read-only. */
    onSelect?: (optionId: number) => void;
    /** Show 1-4 keyboard hints on the options. */
    showKeyHints?: boolean;
    disabled?: boolean;
    /**
     * Render the Chinese translation under the prompt and under each option. The exam
     * runner never sets this, and its DTO carries no translation anyway.
     */
    showTranslation?: boolean;
}

/**
 * The question itself: German prompt, optional picture, and four options.
 *
 * The German is never replaced -- the real exam is in German, so the prompt and the
 * answers stay exactly as the source markdown has them. With `showTranslation` the
 * Chinese rendering appears *underneath* each, visibly secondary. Everything explanatory
 * lives in `AnalysisPanel` instead.
 */
export function QuestionView({
    question,
    selectedOptionId,
    revealed = false,
    onSelect,
    showKeyHints = false,
    disabled = false,
    showTranslation = false
}: QuestionViewProps) {
    const interactive = onSelect !== undefined && !disabled;
    const promptZh = showTranslation && question.textZh ? question.textZh : null;

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
                <Badge tone="neutral">第 {question.number} 题</Badge>
                {question.scope === "STATE" ? <Badge tone="brand">{question.state} 州题</Badge> : null}
                {question.isImageQuestion ? <Badge tone="neutral">图片题</Badge> : null}
                {question.topicSlug ? (
                    <Link to={`/topics/${question.topicSlug}`} className="text-xs text-ink-soft hover:text-brand hover:underline">
                        知识点讲解 →
                    </Link>
                ) : null}
            </div>

            <h2 lang="de" className="text-xl leading-snug font-semibold text-balance text-ink sm:text-2xl">
                {question.text}
            </h2>
            {promptZh ? (
                <p lang="zh-Hans" className="-mt-2 text-base leading-relaxed text-ink-soft">
                    {promptZh}
                </p>
            ) : null}

            {question.imageUrl ? (
                <img
                    src={question.imageUrl}
                    alt={`第 ${question.number} 题配图：四个选项的图片`}
                    className="w-full max-w-2xl rounded-xl border border-line bg-white"
                    loading="lazy"
                />
            ) : null}

            <ul className="space-y-2.5">
                {question.options.map((option, index) => {
                    const isSelected = selectedOptionId === option.id;
                    const isCorrect = revealed && option.isCorrect === true;
                    const isWrongPick = revealed && isSelected && option.isCorrect !== true;

                    return (
                        <li key={option.id}>
                            <button
                                type="button"
                                disabled={!interactive}
                                onClick={interactive ? () => onSelect(option.id) : undefined}
                                aria-pressed={isSelected}
                                className={cx(
                                    "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                                    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
                                    interactive && "hover:border-brand hover:bg-brand-soft/40 cursor-pointer",
                                    !interactive && "cursor-default",
                                    isCorrect
                                        ? "border-good bg-good-soft"
                                        : isWrongPick
                                          ? "border-bad bg-bad-soft"
                                          : isSelected
                                            ? "border-brand bg-brand-soft"
                                            : "border-line bg-surface"
                                )}
                            >
                                <span
                                    className={cx(
                                        "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-lg border text-xs font-semibold",
                                        isCorrect
                                            ? "border-good text-good"
                                            : isWrongPick
                                              ? "border-bad text-bad"
                                              : isSelected
                                                ? "border-brand text-brand"
                                                : "border-line text-ink-soft"
                                    )}
                                >
                                    {showKeyHints ? index + 1 : optionLetter(index)}
                                </span>

                                <span className="flex-1">
                                    <span lang="de" className="block text-[15px] leading-relaxed text-ink">
                                        {question.isImageQuestion ? `图 ${option.text}` : option.text}
                                    </span>
                                    {showTranslation && option.textZh && !question.isImageQuestion ? (
                                        <span lang="zh-Hans" className="block text-sm leading-relaxed text-ink-soft">
                                            {option.textZh}
                                        </span>
                                    ) : null}
                                </span>

                                {isCorrect ? (
                                    <Check className="mt-0.5 size-5 shrink-0 text-good" aria-label="正确答案" />
                                ) : null}
                                {isWrongPick ? (
                                    <X className="mt-0.5 size-5 shrink-0 text-bad" aria-label="你的选择（错误）" />
                                ) : null}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
